import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Fade,
  IconButton,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import PlaceIcon from "@mui/icons-material/Place";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/config";
import { parseUiLocaleFromSearch } from "../../i18n/urlLocale";
import {
  webchatExchange,
  webchatGetMessages,
  webchatPostMessage,
  webchatPostPresence,
  webchatSignUpload,
  setWebchatSession,
  getWebchatToken,
  getWebchatCompany,
  getWebchatSessionMeta,
  clearWebchatSession,
  isWebchatClosedError,
  WEBCHAT_ERROR,
  getWebchatErrorCode,
  ensureWebchatSessionFresh,
} from "../../api/webchat";
import { useComposerAudioRecording } from "../../components/MessageHistory/useComposerAudioRecording";
import AudioPreview from "../../components/MessageHistory/AudioPreview";
import WebChatMessageBubble from "./WebChatMessageBubble";
import {
  buildConversationImageGallery,
  getConversationImageGalleryIndex,
} from "../../components/MessageHistory/ImageMessage";
import {
  unlockWebchatVisitorAudio,
  ensureWebchatNotificationPermission,
  notifyWebchatAgentMessages,
} from "./webchatVisitorAlerts";
import WebchatPwaEscapeBanner from "./WebchatPwaEscapeBanner";
import { isWebchatStandaloneDisplay } from "./webchatStandalone";
import TicketFlowWizard from "../TicketFlow/TicketFlowWizard";

/**
 * Idioma do Web Chat público: ?lang= / ?locale= …, senão pt-BR.
 * Não usa xpro4_ui_locale (preferência do painel admin) para não exibir chrome em inglês
 * quando o navegador/localStorage do operador está em en.
 */
function resolveWebchatPublicLocale(search) {
  const fromUrl = parseUiLocaleFromSearch(search || "");
  if (fromUrl) return fromUrl;
  const supported = Array.isArray(i18n.options?.supportedLngs)
    ? i18n.options.supportedLngs.filter((c) => c && c !== "cimode")
    : [];
  if (supported.includes("pt-BR")) return "pt-BR";
  return supported[0] || "pt-BR";
}
/** Wallpaper estilo WhatsApp: base bege + doodles leves em SVG (sem asset externo). */
const WA_WALLPAPER = {
  backgroundColor: "#e5ddd5",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='260' viewBox='0 0 260 260'%3E%3Cg fill='none' stroke='%23cfc5b8' stroke-width='1.2' stroke-opacity='0.55'%3E%3Ccircle cx='28' cy='36' r='7'/%3E%3Cpath d='M48 32c8 0 12 6 12 12s-4 12-12 12'/%3E%3Cpath d='M90 28l8 14h-16z'/%3E%3Crect x='120' y='24' width='18' height='14' rx='2'/%3E%3Cpath d='M160 22c6 4 6 14 0 18M168 22c6 4 6 14 0 18'/%3E%3Ccircle cx='210' cy='34' r='5'/%3E%3Cpath d='M220 34h16M228 26v16'/%3E%3Cpath d='M24 90c10-4 18 6 10 14s-18 2-14-8'/%3E%3Cpath d='M70 88h20M80 78v20'/%3E%3Ccircle cx='130' cy='96' r='9'/%3E%3Cpath d='M160 86l12 8-12 8z'/%3E%3Cpath d='M200 90c8 0 14 6 14 12h-28c0-6 6-12 14-12z'/%3E%3Crect x='30' y='150' width='12' height='20' rx='2'/%3E%3Cpath d='M60 148c10 0 16 8 16 16s-6 16-16 16'/%3E%3Ccircle cx='110' cy='164' r='6'/%3E%3Cpath d='M130 152l10 22M150 152l-10 22'/%3E%3Cpath d='M180 158h28M194 144v28'/%3E%3Cpath d='M230 150c0 10-8 18-18 18'/%3E%3Cpath d='M40 220c6-8 18-8 24 0'/%3E%3Ccircle cx='90' cy='224' r='8'/%3E%3Cpath d='M120 214h22v16h-22z'/%3E%3Cpath d='M160 220c8 4 16 4 24 0'/%3E%3Cpath d='M210 212l14 14M224 212l-14 14'/%3E%3C/g%3E%3C/svg%3E\")",
};

const DESKTOP_PANEL_MAX = 720;
const PAGE_SIZE = 40;
const WA_TEAL = "#00a884";
const HEADER_BG = "#075e54";

function messageHasInteractive(m) {
  const meta = m?.chatbotMetadata;
  if (meta && typeof meta === "object") {
    if (Array.isArray(meta.buttons) && meta.buttons.length) return true;
    if (Array.isArray(meta.linkButtons) && meta.linkButtons.length) return true;
    if (meta.listMessage || meta.buttonMessage) return true;
    if (meta.interactiveOptions?.ctaUrl || meta.interactiveOptions?.url) return true;
    if (meta.interactiveOptions?.mode === "ticket_flow_web" && meta.interactiveOptions?.flowToken) {
      return true;
    }
  }
  const raw = m?.json_whatsapp;
  if (!raw) return false;
  try {
    const j = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!j || typeof j !== "object") return false;
    if (j.interactive || j.buttonMessage || j.listMessage || j.buttons || j.botoes) return true;
    if (j.type === "interactive" || j.type === "button" || j.type === "list") return true;
  } catch {
    return false;
  }
  return false;
}

function lastInteractiveAgentMessageId(messages) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m?.isFromClient || m?.sender === "client") continue;
    if (messageHasInteractive(m)) return m.id;
  }
  return null;
}

function resolveAccessKindFromError(error) {
  const code = getWebchatErrorCode(error);
  if (code === WEBCHAT_ERROR.CLOSED || isWebchatClosedError(error) || error?.response?.status === 410) {
    return "closed";
  }
  return "unavailable";
}

function isOptimisticMessage(m) {
  if (!m) return false;
  if (m._optimistic) return true;
  const id = m.id;
  return typeof id === "string" && id.startsWith("tmp-");
}

function numericMessageId(m) {
  const n = Number(m?.id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function lastNumericMessageId(messages) {
  for (let i = (messages || []).length - 1; i >= 0; i -= 1) {
    const n = numericMessageId(messages[i]);
    if (n != null) return n;
  }
  return null;
}

function sortMessagesById(list) {
  return [...(list || [])].sort((a, b) => {
    const na = numericMessageId(a);
    const nb = numericMessageId(b);
    if (na != null && nb != null) return na - nb;
    if (na != null) return -1;
    if (nb != null) return 1;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  });
}

/** Merge por id; ao receber dados do servidor, descarta optimistic/tmp-* locais. */
function mergeById(older, newer, { dropOptimistic = false } = {}) {
  const map = new Map();
  for (const m of older || []) {
    if (dropOptimistic && isOptimisticMessage(m)) continue;
    map.set(m.id, m);
  }
  for (const m of newer || []) {
    if (dropOptimistic && isOptimisticMessage(m)) continue;
    map.set(m.id, m);
  }
  if (dropOptimistic) {
    for (const id of [...map.keys()]) {
      if (isOptimisticMessage(map.get(id))) map.delete(id);
    }
  }
  return sortMessagesById(Array.from(map.values()));
}

function replaceOptimisticWithServer(prev, optimisticId, serverId, patch = {}) {
  const sid = Number(serverId);
  return sortMessagesById(
    (prev || []).map((m) => {
      if (m.id !== optimisticId) return m;
      if (!Number.isFinite(sid) || sid <= 0) {
        return { ...m, ...patch, _optimistic: false };
      }
      return {
        ...m,
        ...patch,
        id: sid,
        _optimistic: false,
        deliveryStatus: patch.deliveryStatus || { status: 2, label: "Entregue" },
      };
    })
  );
}

function StatusScreen({ kind, brandName, onRestart }) {
  const { t } = useTranslation("chat");
  const isClosed = kind === "closed";
  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "#0b141a",
        backgroundImage: "radial-gradient(ellipse at top, #1a3a34 0%, #0b141a 55%)",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 420,
          bgcolor: "#fff",
          borderRadius: 2,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          boxShadow: "0 8px 32px rgba(0,0,0,0.28)",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: isClosed ? "rgba(7,94,84,0.12)" : "rgba(211,47,47,0.1)",
            color: isClosed ? HEADER_BG : "#c62828",
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          {isClosed ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
          ) : (
            <ErrorOutlineIcon sx={{ fontSize: 32 }} />
          )}
        </Box>
        {brandName ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {brandName}
          </Typography>
        ) : null}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3 }}>
          {isClosed ? t("webchat.closedTitle") : t("webchat.unavailableTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {isClosed ? t("webchat.closedBody") : t("webchat.unavailableBody")}
        </Typography>
        {isClosed && onRestart ? (
          <Button variant="contained" onClick={onRestart} sx={{ mt: 2 }}>
            {t("webchat.startNewConversation")}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

export default function WebChatPage() {
  const { t, i18n: i18nInstance } = useTranslation("chat");
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const companyFromQuery = (searchParams.get("company") || "").trim().toLowerCase();
  const widgetKey = (searchParams.get("key") || "").trim();

  // Chrome do Web Chat em pt-BR por padrão (ou ?lang=), sem herdar locale do painel.
  useLayoutEffect(() => {
    const next = resolveWebchatPublicLocale(window.location.search);
    if (next && i18nInstance.language !== next) {
      i18nInstance.changeLanguage(next);
    }
  }, [i18nInstance, searchParams]);

  const [accessKind, setAccessKind] = useState(null);
  const [booting, setBooting] = useState(true);
  const [meta, setMeta] = useState(() => getWebchatSessionMeta());
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [closedDuringChat, setClosedDuringChat] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine !== false : true
  );
  const [toast, setToast] = useState(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [hasMoreEarlier, setHasMoreEarlier] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const [ticketFlowToken, setTicketFlowToken] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [attachOpen, setAttachOpen] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [standalonePwa] = useState(() =>
    typeof window === "undefined" ? false : isWebchatStandaloneDisplay()
  );
  const unreadAnchorIdRef = useRef(null);
  const photoRef = useRef(null);
  const cameraRef = useRef(null);
  const docRef = useRef(null);

  const listRef = useRef(null);
  const pollRef = useRef(null);
  const heartbeatRef = useRef(null);
  const bottomAnchorRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const messagesRef = useRef([]);
  const pendingSendAudioRef = useRef(false);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const showError = useCallback((message) => {
    setToast({ severity: "error", message: String(message || "") });
  }, []);

  const {
    audioURL,
    isRecording,
    recordingDuration,
    startRecording,
    stopRecording,
    discardRecording,
    handleRemoveAudio,
    clearAudio,
  } = useComposerAudioRecording({
    onNotSupported: () => showError(t("webchat.micUnsupported")),
    onPermissionDenied: () => showError(t("webchat.micDenied")),
  });

  const brandName = meta?.branding?.companyName || t("webchat.brandFallback");
  const sendColor = widgetKey && meta?.appearance?.accentColor ? meta.appearance.accentColor : WA_TEAL;
  const restartSiteChat = widgetKey
    ? () => {
        clearWebchatSession();
        sessionStorage.setItem("xproSiteWebchatRestart", "1");
        window.location.reload();
      }
    : null;
  const brandLogo = meta?.branding?.logo || null;
  const workspaceDomain =
    String(meta?.company || getWebchatCompany() || companyFromQuery || "").trim() || brandName;
  const interactiveMsgId = useMemo(() => lastInteractiveAgentMessageId(messages), [messages]);
  const conversationImageGallery = useMemo(() => buildConversationImageGallery(messages), [messages]);
  const readOnly = closedDuringChat;
  const alertLabelsRef = useRef({ brandName, fallbackBody: t("webchat.fallbackMessage") });
  alertLabelsRef.current = {
    brandName,
    fallbackBody: t("webchat.fallbackMessage"),
  };

  const armVisitorAlerts = useCallback(() => {
    unlockWebchatVisitorAudio();
    void ensureWebchatNotificationPermission();
  }, []);

  const alertIncomingAgent = useCallback((agentMessages) => {
    const list = Array.isArray(agentMessages) ? agentMessages : [];
    if (!list.length) return;
    const labels = alertLabelsRef.current;
    notifyWebchatAgentMessages(list, {
      brandName: labels.brandName,
      fallbackBody: labels.fallbackBody,
      multiBody: t("webchat.notifyNewMessages", { count: list.length }),
    });
  }, [t]);

  const handleAccessFailure = useCallback((errorOrKind) => {
    const kind =
      errorOrKind === "closed" || errorOrKind === "unavailable"
        ? errorOrKind
        : resolveAccessKindFromError(errorOrKind);
    if (kind === "closed") {
      if (getWebchatToken() || messagesRef.current.length) {
        setClosedDuringChat(true);
        return;
      }
      clearWebchatSession();
      setAccessKind("unavailable");
      return;
    }
    clearWebchatSession();
    setAccessKind(kind);
  }, []);

  const scrollToBottom = useCallback((smooth = false) => {
    requestAnimationFrame(() => {
      if (bottomAnchorRef.current) {
        bottomAnchorRef.current.scrollIntoView({
          behavior: smooth ? "smooth" : "auto",
          block: "end",
        });
      } else if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  }, []);

  const loadMessages = useCallback(
    async ({ prepend = false, beforeId = null, poll = false } = {}) => {
      if (!getWebchatToken()) return;
      try {
        const params = { limit: PAGE_SIZE };
        if (prepend) {
          const before =
            beforeId ||
            (messagesRef.current[0] != null ? Number(messagesRef.current[0].id) : null);
          if (!before) return;
          params.before = before;
        } else if (poll && messagesRef.current.length) {
          const after = lastNumericMessageId(messagesRef.current);
          if (after != null) {
            params.after = after;
            params.limit = 50;
          }
        }
        const result = await webchatGetMessages(params);
        if (result?.conversation?.closed) {
          setClosedDuringChat(true);
        }
        const incoming = Array.isArray(result?.messages) ? result.messages : [];
        if (prepend) {
          setMessages((prev) => mergeById(incoming, prev, { dropOptimistic: true }));
          setHasMoreEarlier(incoming.length >= PAGE_SIZE);
        } else if (poll && params.after) {
          if (!incoming.length) return;
          const agentFresh = incoming.filter(
            (m) => !(m.isFromClient || m.sender === "client")
          );
          if (agentFresh.length && !stickToBottomRef.current) {
            if (!unreadAnchorIdRef.current) {
              unreadAnchorIdRef.current = agentFresh[0].id;
            }
            setUnreadCount((n) => n + agentFresh.length);
          }
          if (agentFresh.length) alertIncomingAgent(agentFresh);
          setMessages((prev) => mergeById(prev, incoming, { dropOptimistic: true }));
        } else {
          const withoutOptimistic = (messagesRef.current || []).filter(
            (m) => !isOptimisticMessage(m)
          );
          if (!withoutOptimistic.length) {
            setHasMoreEarlier(incoming.length >= PAGE_SIZE);
            setMessages(incoming);
          } else {
            const newestKnown = lastNumericMessageId(withoutOptimistic) || 0;
            const fresh = incoming.filter((m) => Number(m.id) > newestKnown);
            if (!fresh.length) {
              const byId = new Map(incoming.map((m) => [m.id, m]));
              setMessages(withoutOptimistic.map((m) => byId.get(m.id) || m));
            } else {
              const agentFresh = fresh.filter(
                (m) => !(m.isFromClient || m.sender === "client")
              );
              if (agentFresh.length) alertIncomingAgent(agentFresh);
              setMessages(mergeById(withoutOptimistic, fresh, { dropOptimistic: true }));
            }
          }
        }
        if (!prepend && stickToBottomRef.current) scrollToBottom(false);
      } catch (e) {
        const kind = resolveAccessKindFromError(e);
        if (kind === "closed") {
          setClosedDuringChat(true);
          return;
        }
        if (e?.response?.status === 401 || e?.response?.status === 404) {
          handleAccessFailure(e);
        }
        console.warn("[webchat] load messages", e);
      }
    },
    [scrollToBottom, handleAccessFailure, alertIncomingAgent]
  );

  const loadEarlier = useCallback(async () => {
    if (loadingEarlier || !hasMoreEarlier) return;
    const before = messagesRef.current[0] != null ? Number(messagesRef.current[0].id) : null;
    if (!before) return;
    const listEl = listRef.current;
    const prevHeight = listEl?.scrollHeight || 0;
    const prevTop = listEl?.scrollTop || 0;
    setLoadingEarlier(true);
    stickToBottomRef.current = false;
    try {
      await loadMessages({ prepend: true, beforeId: before });
      requestAnimationFrame(() => {
        if (!listEl) return;
        const delta = listEl.scrollHeight - prevHeight;
        listEl.scrollTop = prevTop + delta;
      });
    } finally {
      setLoadingEarlier(false);
    }
  }, [loadingEarlier, hasMoreEarlier, loadMessages]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      setBooting(true);
      setAccessKind(null);
      setClosedDuringChat(false);
      setHasMoreEarlier(true);
      try {
        const existingToken = getWebchatToken();
        const existingCompany = getWebchatCompany();

        if (pathToken && pathToken !== "c") {
          const company = companyFromQuery || existingCompany;
          if (!company) {
            if (!cancelled) {
              clearWebchatSession();
              setAccessKind("unavailable");
            }
            return;
          }
          try {
            const exchanged = await webchatExchange({ token: pathToken, company });
            if (cancelled) return;
            if (exchanged?.conversation?.closed) {
              setClosedDuringChat(true);
            }
            setWebchatSession({
              token: exchanged.token,
              company: exchanged.company || company,
              meta: exchanged,
            });
            setMeta(exchanged);
            navigate(`/webchat/c?company=${encodeURIComponent(exchanged.company || company)}`, {
              replace: true,
            });
          } catch (exErr) {
            if (!cancelled) handleAccessFailure(exErr);
            return;
          }
        } else if (!existingToken || !existingCompany) {
          if (!cancelled) {
            clearWebchatSession();
            setAccessKind("unavailable");
          }
          return;
        } else if (!meta) {
          setMeta(getWebchatSessionMeta());
        }

        await loadMessages();
        ensureWebchatSessionFresh().catch(() => {});
      } catch (e) {
        if (!cancelled) handleAccessFailure(e);
      } finally {
        if (!cancelled) setBooting(false);
      }
    }

    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathToken, companyFromQuery]);

  useEffect(() => {
    if (booting || accessKind) return undefined;
    const isVisible = () =>
      typeof document === "undefined" || document.visibilityState === "visible";
    const refreshTimer = setInterval(() => {
      if (isVisible()) ensureWebchatSessionFresh().catch(() => {});
    }, 50 * 60 * 1000);

    if (closedDuringChat) {
      return () => clearInterval(refreshTimer);
    }

    const sendOnline = () => {
      if (isVisible()) webchatPostPresence({ state: "online" }).catch(() => {});
    };
    const sendAway = ({ keepalive = false } = {}) => {
      webchatPostPresence({ state: "away", keepalive }).catch(() => {});
    };
    let awayTimer = null;
    const clearAwayTimer = () => {
      if (awayTimer) {
        clearTimeout(awayTimer);
        awayTimer = null;
      }
    };
    const sendAwaySoon = ({ keepalive = false } = {}) => {
      if (keepalive) {
        clearAwayTimer();
        sendAway({ keepalive: true });
        return;
      }
      clearAwayTimer();
      awayTimer = setTimeout(() => {
        awayTimer = null;
        sendAway();
      }, 1500);
    };
    const startPoll = () => {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(() => {
        if (online && isVisible()) loadMessages({ poll: true });
      }, 3500);
    };
    const stopPoll = () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
    const startHeartbeat = () => {
      clearInterval(heartbeatRef.current);
      if (isVisible()) {
        clearAwayTimer();
        sendOnline();
        heartbeatRef.current = setInterval(sendOnline, 12_000);
      } else {
        sendAwaySoon();
        heartbeatRef.current = setInterval(() => sendAway(), 25_000);
      }
    };
    const stopHeartbeat = () => {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    };
    const onVisible = () => {
      startPoll();
      startHeartbeat();
      ensureWebchatSessionFresh()
        .catch(() => {})
        .finally(() => loadMessages());
    };
    const onBackground = ({ keepalive = false } = {}) => {
      stopPoll();
      sendAwaySoon({ keepalive });
      startHeartbeat();
    };
    const onVis = () => {
      if (document.visibilityState === "visible") onVisible();
      else onBackground();
    };
    const onHide = () => {
      if (isVisible()) return;
      onBackground({ keepalive: true });
    };
    if (isVisible()) {
      startPoll();
      startHeartbeat();
    } else {
      startHeartbeat();
    }
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("freeze", onHide);
    window.addEventListener("pageshow", onVisible);
    window.addEventListener("focus", sendOnline);
    window.addEventListener("pagehide", onHide);
    return () => {
      stopPoll();
      stopHeartbeat();
      clearAwayTimer();
      clearInterval(refreshTimer);
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("freeze", onHide);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("focus", sendOnline);
      window.removeEventListener("pagehide", onHide);
    };
  }, [booting, accessKind, closedDuringChat, loadMessages, online]);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      loadMessages();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [loadMessages]);

  useEffect(() => {
    if (stickToBottomRef.current) scrollToBottom(true);
  }, [messages.length, scrollToBottom]);

  const uploadBlobAsAudio = async (blobUrl) => {
    const blob = await fetch(blobUrl).then((r) => r.blob());
    const file = new File([blob], `audio-${Date.now()}.mp3`, {
      type: blob.type || "audio/mpeg",
    });
    const sign = await webchatSignUpload({
      filename: file.name,
      contentType: file.type,
    });
    const uploadUrl = sign?.uploadUrl || sign?.url;
    const publicUrl = sign?.fileUrl || sign?.publicUrl || sign?.url;
    if (!uploadUrl || !publicUrl) throw new Error("sign failed");
    const putRes = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
    if (!putRes.ok) throw new Error(`upload_put_failed_${putRes.status}`);
    await webchatPostMessage({ type: "audio", audio: publicUrl, message: "Áudio" });
  };

  const sendText = async (text) => {
    const body = String(text || "").trim();
    if (!body || sending || readOnly || !online) return;
    armVisitorAlerts();
    setSending(true);
    const optimistic = {
      id: `tmp-${Date.now()}`,
      text: body,
      type: "text",
      isFromClient: true,
      sender: "client",
      createdAt: new Date().toISOString(),
      deliveryStatus: { status: 0, label: "Enviando" },
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    stickToBottomRef.current = true;
    try {
      const posted = await webchatPostMessage({ type: "text", message: body });
      const serverId = posted?.id;
      setMessages((prev) => replaceOptimisticWithServer(prev, optimistic.id, serverId));
      await loadMessages();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setDraft(body);
      if (resolveAccessKindFromError(e) === "closed") {
        setClosedDuringChat(true);
        return;
      }
      if (e?.response?.status === 401 || e?.response?.status === 404) {
        handleAccessFailure(e);
        return;
      }
      showError(t("webchat.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    armVisitorAlerts();
    if (audioURL) {
      if (sending || readOnly || !online) return;
      setSending(true);
      try {
        await uploadBlobAsAudio(audioURL);
        clearAudio();
        stickToBottomRef.current = true;
        await loadMessages();
      } catch (e) {
        if (resolveAccessKindFromError(e) === "closed") {
          setClosedDuringChat(true);
          return;
        }
        if (e?.response?.status === 401 || e?.response?.status === 404) {
          handleAccessFailure(e);
          return;
        }
        showError(t("webchat.uploadFailed"));
      } finally {
        setSending(false);
      }
      return;
    }
    sendText(draft);
  };

  useEffect(() => {
    if (!pendingSendAudioRef.current || !audioURL || isRecording) return;
    pendingSendAudioRef.current = false;
    handleSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioURL, isRecording]);

  const handleQuickReply = (label) => {
    if (readOnly) return;
    sendText(label);
  };

  const handleSendLocation = () => {
    if (sending || readOnly || !online) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      showError(t("webchat.locationUnsupported"));
      return;
    }
    setSending(true);
    stickToBottomRef.current = true;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const latitude = Number(pos?.coords?.latitude);
        const longitude = Number(pos?.coords?.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          setSending(false);
          showError(t("webchat.locationDenied"));
          return;
        }
        const optimistic = {
          id: `tmp-loc-${Date.now()}`,
          type: "location",
          text: t("webchat.locationLabel"),
          latitude,
          longitude,
          isFromClient: true,
          sender: "client",
          createdAt: new Date().toISOString(),
          deliveryStatus: { status: 0, label: "Enviando" },
          _optimistic: true,
        };
        setMessages((prev) => [...prev, optimistic]);
        try {
          const posted = await webchatPostMessage({
            type: "location",
            latitude,
            longitude,
            message: t("webchat.locationLabel"),
          });
          setMessages((prev) => replaceOptimisticWithServer(prev, optimistic.id, posted?.id));
          await loadMessages();
        } catch (e) {
          setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
          if (resolveAccessKindFromError(e) === "closed") {
            setClosedDuringChat(true);
            return;
          }
          if (e?.response?.status === 401 || e?.response?.status === 404) {
            handleAccessFailure(e);
            return;
          }
          showError(t("webchat.sendFailed"));
        } finally {
          setSending(false);
        }
      },
      () => {
        setSending(false);
        showError(t("webchat.locationDenied"));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const handleSendContact = async () => {
    const name = String(contactName || "").trim();
    const phone = String(contactPhone || "").trim();
    if (!name || !phone || sending || readOnly || !online) return;
    setSending(true);
    stickToBottomRef.current = true;
    const optimistic = {
      id: `tmp-contact-${Date.now()}`,
      type: "contact",
      text: "",
      contact: { name, phone },
      isFromClient: true,
      sender: "client",
      createdAt: new Date().toISOString(),
      deliveryStatus: { status: 0, label: "Enviando" },
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setContactOpen(false);
    setContactName("");
    setContactPhone("");
    try {
      const posted = await webchatPostMessage({ type: "contact", name, phone });
      setMessages((prev) => replaceOptimisticWithServer(prev, optimistic.id, posted?.id));
      await loadMessages();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      if (resolveAccessKindFromError(e) === "closed") {
        setClosedDuringChat(true);
        return;
      }
      if (e?.response?.status === 401 || e?.response?.status === 404) {
        handleAccessFailure(e);
        return;
      }
      showError(t("webchat.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || sending || readOnly || !online) return;
    setSending(true);
    stickToBottomRef.current = true;
    try {
      const sign = await webchatSignUpload({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
      });
      const uploadUrl = sign?.uploadUrl || sign?.url;
      const publicUrl = sign?.fileUrl || sign?.publicUrl || sign?.url;
      if (!uploadUrl || !publicUrl) throw new Error("sign failed");
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!putRes.ok) throw new Error(`upload_put_failed_${putRes.status}`);
      const isImage = /^image\//i.test(file.type);
      const isVideo = /^video\//i.test(file.type);
      const isAudio = /^audio\//i.test(file.type);
      if (isImage) {
        await webchatPostMessage({ type: "image", image: publicUrl, message: file.name });
      } else if (isVideo) {
        await webchatPostMessage({ type: "video", video: publicUrl, message: file.name });
      } else if (isAudio) {
        await webchatPostMessage({ type: "audio", audio: publicUrl, message: file.name });
      } else {
        await webchatPostMessage({
          type: "file",
          file: { url: publicUrl, name: file.name },
          message: file.name,
        });
      }
      await loadMessages();
    } catch (e) {
      if (resolveAccessKindFromError(e) === "closed") {
        setClosedDuringChat(true);
        return;
      }
      if (e?.response?.status === 401 || e?.response?.status === 404) {
        handleAccessFailure(e);
        return;
      }
      showError(t("webchat.uploadFailed"));
    } finally {
      setSending(false);
    }
  };

  const onListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    stickToBottomRef.current = nearBottom;
    setShowScrollDown(!nearBottom);
    if (nearBottom) {
      setUnreadCount(0);
      unreadAnchorIdRef.current = null;
    }
    if (el.scrollTop < 48 && hasMoreEarlier && !loadingEarlier) {
      loadEarlier();
    }
  };

  const jumpToBottom = () => {
    stickToBottomRef.current = true;
    setUnreadCount(0);
    unreadAnchorIdRef.current = null;
    setShowScrollDown(false);
    scrollToBottom(true);
  };

  const formatRecordingTime = (seconds) => {
    const m = Math.floor(Number(seconds || 0) / 60);
    const s = Number(seconds || 0) % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const openAttach = (kind) => {
    setAttachOpen(false);
    if (kind === "photo") photoRef.current?.click();
    else if (kind === "camera") cameraRef.current?.click();
    else if (kind === "doc") docRef.current?.click();
    else if (kind === "location") handleSendLocation();
    else if (kind === "contact") setContactOpen(true);
  };

  if (booting) {
    const bootAccent = widgetKey && meta?.appearance?.accentColor ? meta.appearance.accentColor : "#25d366";
    return (
      <Box
        sx={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          bgcolor: widgetKey ? "#fff" : "#0b141a",
          background: widgetKey
            ? `linear-gradient(165deg, ${bootAccent}12 0%, #ffffff 45%, #f8fafc 100%)`
            : undefined,
        }}
      >
        <CircularProgress size={28} thickness={4.5} sx={{ color: bootAccent }} />
        {widgetKey ? (
          <Typography sx={{ fontSize: "0.875rem", color: "#667085", fontWeight: 500 }}>
            {t("webchat.loadingConversation", { defaultValue: "Carregando conversa…" })}
          </Typography>
        ) : null}
      </Box>
    );
  }

  if (accessKind === "unavailable") {
    return <StatusScreen kind={accessKind} brandName={brandName} onRestart={restartSiteChat} />;
  }

  const canSendText = Boolean(draft.trim()) && !isRecording;
  const canSendAudio = Boolean(audioURL) && !isRecording;
  const showSend = canSendText || canSendAudio;

  const attachItems = [
    {
      key: "photo",
      label: t("webchat.attachPhoto"),
      icon: <PhotoLibraryIcon />,
      color: "#007AFF",
      bg: "#E8F1FF",
    },
    {
      key: "camera",
      label: t("webchat.attachCamera"),
      icon: <PhotoCameraIcon />,
      color: "#54656f",
      bg: "#fff",
    },
    {
      key: "location",
      label: t("webchat.attachLocation"),
      icon: <PlaceIcon />,
      color: "#00a884",
      bg: "#fff",
    },
    {
      key: "contact",
      label: t("webchat.attachContact"),
      icon: <PersonOutlineIcon />,
      color: "#54656f",
      bg: "#fff",
    },
    {
      key: "doc",
      label: t("webchat.attachDocument"),
      icon: <InsertDriveFileOutlinedIcon />,
      color: "#5B6BFF",
      bg: "#fff",
    },
  ];

  const chatPanel = (
    <Box
      sx={{
        width: "100%",
        maxWidth: widgetKey ? "100%" : { xs: "100%", sm: DESKTOP_PANEL_MAX },
        height: widgetKey ? "100dvh" : { xs: "100dvh", sm: "min(900px, calc(100dvh - 32px))" },
        maxHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRadius: widgetKey ? 0 : { xs: 0, sm: 2 },
        overflow: "hidden",
        boxShadow: widgetKey ? "none" : { xs: "none", sm: "0 8px 32px rgba(0,0,0,0.28)" },
        position: "relative",
      }}
    >
      <Box
        component="header"
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexShrink: 0,
          pt: { xs: "max(10px, env(safe-area-inset-top))", sm: 1 },
          bgcolor: widgetKey ? "rgba(255,255,255,0.92)" : "rgba(247,247,247,0.92)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            bgcolor: brandLogo ? "transparent" : widgetKey ? `${sendColor}22` : "#AED5F5",
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: "1rem",
            flexShrink: 0,
            overflow: "hidden",
            color: widgetKey ? sendColor : "#1b5a8a",
            boxShadow: widgetKey ? `0 0 0 2px ${sendColor}28` : "none",
            position: "relative",
          }}
        >
          {brandLogo ? (
            <Box
              component="img"
              src={brandLogo}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            (brandName || "A").charAt(0).toUpperCase()
          )}
          {widgetKey && online ? (
            <Box
              sx={{
                position: "absolute",
                right: 1,
                bottom: 1,
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "#12B76A",
                border: "2px solid #fff",
              }}
            />
          ) : null}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.2, color: "#111b21", fontSize: "1.02rem", letterSpacing: "-0.01em" }} noWrap>
            {brandName}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: online ? "#667781" : "#b42318", mt: 0.15 }} noWrap>
            {online
              ? widgetKey
                ? t("webchat.onlineStatus", { defaultValue: "Online agora" })
                : workspaceDomain
              : t("webchat.offlineBanner")}
          </Typography>
        </Box>
      </Box>

      {standalonePwa && !widgetKey ? <WebchatPwaEscapeBanner /> : null}

      {!online ? (
        <Alert
          icon={<WifiOffIcon fontSize="inherit" />}
          severity="warning"
          sx={{ borderRadius: 0, py: 0.25 }}
        >
          {t("webchat.offlineBanner")}
        </Alert>
      ) : null}

      <Box sx={{ flex: 1, position: "relative", minHeight: 0 }}>
        <Box
          ref={listRef}
          onScroll={onListScroll}
          sx={{
            height: "100%",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            ...WA_WALLPAPER,
            py: 1,
          }}
        >
          <Box sx={{ px: 2, pb: 1, textAlign: "center" }}>
            {loadingEarlier ? (
              <Typography variant="caption" sx={{ color: "#667781" }}>
                {t("webchat.loadingEarlier")}
              </Typography>
            ) : hasMoreEarlier && messages.length > 0 ? (
              <Button size="small" onClick={loadEarlier} sx={{ textTransform: "none", color: "#027eb5" }}>
                {t("webchat.loadEarlier")}
              </Button>
            ) : messages.length > 0 ? (
              <Typography variant="caption" sx={{ color: "#667781" }}>
                {t("webchat.noMoreMessages")}
              </Typography>
            ) : null}
          </Box>

          {messages.length === 0 ? (
            <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#667781" }}>
                {t("webchat.emptyHint")}
              </Typography>
            </Box>
          ) : (
            messages.map((m) => (
              <Box key={m.id}>
                {unreadAnchorIdRef.current != null &&
                String(m.id) === String(unreadAnchorIdRef.current) &&
                unreadCount > 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", my: 1.25, px: 2 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.45,
                        borderRadius: 999,
                        bgcolor: "rgba(17,27,33,0.45)",
                        color: "#fff",
                        fontSize: "0.78rem",
                        fontWeight: 500,
                      }}
                    >
                      {t("webchat.unreadBanner", { count: unreadCount })}
                    </Box>
                  </Box>
                ) : null}
                <WebChatMessageBubble
                  msg={m}
                  showInteractive={m.id === interactiveMsgId && !m.isFromClient && !readOnly}
                  sending={sending || readOnly}
                  onQuickReply={handleQuickReply}
                  onOpenTicketFlow={setTicketFlowToken}
                  galleryUrls={conversationImageGallery}
                  galleryIndex={getConversationImageGalleryIndex(messages, m)}
                />
              </Box>
            ))
          )}
          <Box ref={bottomAnchorRef} sx={{ height: 10 }} />
        </Box>

        <Fade in={showScrollDown}>
          <IconButton
            onClick={jumpToBottom}
            aria-label={t("webchat.scrollToBottom")}
            sx={{
              position: "absolute",
              right: 14,
              bottom: 12,
              bgcolor: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              width: 36,
              height: 36,
              "&:hover": { bgcolor: "#f5f6f6" },
            }}
          >
            <KeyboardArrowDownIcon sx={{ color: "#54656f" }} />
          </IconButton>
        </Fade>
      </Box>

      <Box
        component="footer"
        sx={{
          bgcolor: "rgba(240,242,245,0.92)",
          backdropFilter: "blur(12px)",
          px: { xs: 0.75, sm: 1 },
          pt: 0.75,
          pb: { xs: "max(8px, env(safe-area-inset-bottom))", sm: 0.85 },
          flexShrink: 0,
        }}
      >
        {readOnly ? (
          <Box sx={{ px: 1.5, py: 1.25, textAlign: "center" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111b21" }}>
              {t("webchat.closedTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "#667781", mt: 0.5, lineHeight: 1.45 }}>
              {t("webchat.closedBody")}
            </Typography>
            {restartSiteChat ? (
              <Button variant="contained" onClick={restartSiteChat} sx={{ mt: 1.25 }}>
                {t("webchat.startNewConversation")}
              </Button>
            ) : null}
          </Box>
        ) : isRecording ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, py: 0.35 }}>
            <IconButton onClick={discardRecording} aria-label={t("webchat.discardAudioAria")} sx={{ color: "#54656f" }}>
              <DeleteOutlineIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 600, minWidth: 36, color: "#111b21" }}>
              {formatRecordingTime(recordingDuration)}
            </Typography>
            <IconButton
              onClick={stopRecording}
              aria-label={t("webchat.stopRecordAria")}
              sx={{ bgcolor: "#e53935", color: "#fff", "&:hover": { bgcolor: "#c62828" } }}
            >
              <StopRoundedIcon />
            </IconButton>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.35, px: 0.5, overflow: "hidden" }}>
              {Array.from({ length: 28 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 3,
                    height: 6 + ((i * 7 + recordingDuration * 3) % 14),
                    borderRadius: 1,
                    bgcolor: "rgba(0,0,0,0.22)",
                  }}
                />
              ))}
            </Box>
            <IconButton
              onClick={() => {
                pendingSendAudioRef.current = true;
                stopRecording();
              }}
              disabled={sending || !online}
              aria-label={t("webchat.sendAudioAria")}
              sx={{ bgcolor: sendColor, color: "#fff", "&:hover": { bgcolor: sendColor } }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}

        {!readOnly && audioURL && !isRecording ? (
          <Box sx={{ px: 0.5, pb: 0.75 }}>
            <AudioPreview audioURL={audioURL} onRemove={handleRemoveAudio} />
          </Box>
        ) : null}

        {!readOnly && !isRecording ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <input ref={photoRef} type="file" hidden accept="image/*,video/*" onChange={handleFile} />
            <input ref={cameraRef} type="file" hidden accept="image/*,video/*" capture="environment" onChange={handleFile} />
            <input
              ref={docRef}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,audio/*,image/*,video/*"
              onChange={handleFile}
            />
            <IconButton
              onClick={() => setAttachOpen(true)}
              disabled={sending || readOnly || !online || Boolean(audioURL)}
              aria-label={t("webchat.attachAria")}
              sx={{ color: "#54656f", alignSelf: "center", flexShrink: 0 }}
            >
              {attachOpen ? <CloseIcon /> : <AddIcon />}
            </IconButton>
            {!audioURL ? (
              <TextField
                fullWidth
                size="small"
                placeholder={t("webchat.messagePlaceholder")}
                value={draft}
                onFocus={armVisitorAlerts}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                multiline
                maxRows={isMobile ? 4 : 6}
                disabled={sending || readOnly || !online}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 5,
                  alignSelf: "center",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 5,
                    minHeight: 40,
                    alignItems: "center",
                    py: 0.5,
                    px: 1.25,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.75,
                    lineHeight: 1.35,
                  },
                  "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                }}
              />
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
            {showSend ? (
              <Fade in>
                <IconButton
                  onClick={handleSend}
                  disabled={sending || readOnly || !online}
                  aria-label={audioURL ? t("webchat.sendAudioAria") : t("webchat.sendAria")}
                  sx={{
                    alignSelf: "center",
                    flexShrink: 0,
                    bgcolor: sendColor,
                    color: "#fff",
                    "&:hover": { bgcolor: sendColor },
                    "&.Mui-disabled": { bgcolor: "#b0bec5", color: "#fff" },
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Fade>
            ) : (
              <IconButton
                onClick={startRecording}
                disabled={sending || readOnly || !online}
                aria-label={t("webchat.recordAria")}
                sx={{ color: "#54656f", alignSelf: "center", flexShrink: 0 }}
              >
                <MicIcon />
              </IconButton>
            )}
          </Box>
        ) : null}
      </Box>

      <Drawer
        anchor="bottom"
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "18px 18px 0 0",
            bgcolor: "rgba(236,236,238,0.96)",
            backdropFilter: "blur(18px)",
            pb: "max(16px, env(safe-area-inset-bottom))",
            pt: 1.5,
            px: 1.5,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 1.5,
            px: 0.5,
            pb: 1,
          }}
        >
          {attachItems.map((item) => (
            <Box
              key={item.key}
              component="button"
              type="button"
              onClick={() => openAttach(item.key)}
              disabled={sending || readOnly || !online}
              style={{ border: 0, background: "transparent", cursor: "pointer", padding: 0 }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: "50%",
                    bgcolor: item.bg,
                    color: item.color,
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  }}
                >
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: "0.72rem", color: "#111b21", lineHeight: 1.15 }}>
                  {item.label}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Drawer>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        height: "100dvh",
        display: "flex",
        alignItems: { xs: "stretch", sm: widgetKey ? "stretch" : "center" },
        justifyContent: "center",
        bgcolor: widgetKey ? "#fff" : "#0b141a",
        backgroundImage: widgetKey
          ? "none"
          : {
              xs: "none",
              sm: "radial-gradient(ellipse at top, #1a3a34 0%, #0b141a 55%)",
            },
        p: widgetKey ? 0 : { xs: 0, sm: 2 },
        boxSizing: "border-box",
      }}
    >
      {chatPanel}
      <Drawer
        anchor="bottom"
        open={Boolean(ticketFlowToken)}
        onClose={() => setTicketFlowToken("")}
        PaperProps={{
          sx: {
            height: { xs: "100dvh", sm: "92dvh" },
            maxWidth: DESKTOP_PANEL_MAX,
            mx: "auto",
            width: "100%",
            borderRadius: { xs: 0, sm: "16px 16px 0 0" },
          },
        }}
      >
        {ticketFlowToken ? (
          <TicketFlowWizard
            token={ticketFlowToken}
            embedded
            onClose={() => setTicketFlowToken("")}
          />
        ) : null}
      </Drawer>
      <Dialog open={contactOpen} onClose={() => !sending && setContactOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t("webchat.shareContactTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField
            autoFocus
            label={t("webchat.shareContactName")}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label={t("webchat.shareContactPhone")}
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            fullWidth
            size="small"
            placeholder="+55…"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)} disabled={sending}>
            {t("webchat.close")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSendContact}
            disabled={sending || !contactName.trim() || !contactPhone.trim()}
          >
            {t("webchat.sendAria")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity || "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
